// Feature definitions with detailed descriptions and workflows
import {
  FaUsers,
  FaChartBar,
  FaMedal,
  FaTrophy,
  FaChartLine,
  FaAward,
  FaQuestionCircle,
  FaCalendar,
  FaRocket,
  FaHandshake,
} from "react-icons/fa";

export const features = [
  {
    id: "team-announcement",
    name: "Team Announcement",
    shortDescription: "Select your match squad and announce your team in style.",
    icon: FaUsers,
    fullDescription:
      "Create professional team lineup announcements in seconds. Select your playing eleven from your club roster, assign captain and wicket-keeper roles, and generate stunning graphics ready to share on social media.",
    workflow: [
      "Select a match from your upcoming fixtures",
      "Choose your team size and players from your club roster",
      "Assign captain and wicket-keeper roles",
      "Select from professional templates",
      "Customize colors, sponsor logos, and styling",
      "Generate and download your team announcement",
      "Share instantly on social media",
    ],
    useCase:
      "Perfect for announcing your team 24 hours before a match, building excitement among supporters, and showcasing your squad selection with a professional look.",
  },
  {
    id: "match-summary",
    name: "Match Summary",
    shortDescription: "Share key moments, scores, and match highlights.",
    icon: FaChartBar,
    fullDescription:
      "Automatically generate comprehensive match summaries with scores, statistics, and key highlights. Connect with Play-Cricket to pull match data automatically, then customize and share engaging post-match content.",
    workflow: [
      "Select a completed match from your fixtures",
      "Data automatically syncs from Play-Cricket",
      "Choose from pre-designed match summary templates",
      "Review auto-populated statistics and scores",
      "Customize highlights and key moments",
      "Add sponsor logos and branding",
      "Generate and share your match summary",
    ],
    useCase:
      "Ideal for post-match social media posts, celebrating victories, highlighting standout performances, and keeping supporters engaged with comprehensive match reports.",
  },
  {
    id: "top-performance",
    name: "Top Performance",
    shortDescription: "Showcase top players with personalized posts.",
    icon: FaMedal,
    fullDescription:
      "Celebrate standout individual performances with personalized graphics. Highlight player achievements, statistics, and milestones from any match with customizable templates designed to make your players shine.",
    workflow: [
      "Select a match to review performances",
      "Choose a player from the match squad",
      "Select relevant statistics (runs, wickets, catches, etc.)",
      "Pick from player performance templates",
      "Customize title and statistics display",
      "Add player photo and sponsor information",
      "Generate and share the performance post",
    ],
    useCase:
      "Perfect for recognizing Player of the Match, highlighting centuries or five-wicket hauls, celebrating career-best performances, and engaging fans with individual achievements.",
  },
  {
    id: "milestones",
    name: "Milestones",
    shortDescription: "Celebrate career and club achievements.",
    icon: FaTrophy,
    fullDescription:
      "Automatically detect and celebrate player milestones including 100th appearances, career runs/wickets milestones, captaincy debuts, and other significant achievements. Create memorable posts for these special moments.",
    workflow: [
      "View automatically detected milestones for your club",
      "Filter by club or career achievements",
      "Select a milestone to create a post",
      "Choose from milestone celebration templates",
      "Customize achievement details and messaging",
      "Add player photos and club branding",
      "Generate and share the milestone announcement",
    ],
    useCase:
      "Essential for celebrating 100th matches, 1000-run milestones, 50-wicket achievements, captaincy debuts, and other significant career or club accomplishments that deserve special recognition.",
  },
  {
    id: "team-standings",
    name: "Team Standings",
    shortDescription: "Display team standings and league tables.",
    icon: FaChartLine,
    fullDescription:
      "Create professional league table graphics showing your team's position, points, wins, and other standings data. Perfect for mid-season updates and end-of-season final tables.",
    workflow: [
      "Select your league or competition",
      "Choose the season or current standings",
      "Select from league table templates",
      "Customize which teams to display",
      "Adjust colors and styling to match your brand",
      "Add sponsor logos and branding",
      "Generate and share your standings graphic",
    ],
    useCase:
      "Great for mid-season updates showing league position, end-of-season final tables, celebrating promotion or championship wins, and keeping supporters informed about your team's progress.",
  },
  {
    id: "leaderboard",
    name: "Leaderboard",
    shortDescription: "View player statistics and rankings.",
    icon: FaAward,
    fullDescription:
      "Generate leaderboard graphics showcasing top run-scorers, wicket-takers, and other player statistics. Create engaging content highlighting your club's top performers across different categories.",
    workflow: [
      "Select statistics category (runs, wickets, catches, etc.)",
      "Choose season or time period",
      "Pick from leaderboard template designs",
      "Customize number of players to display",
      "Adjust styling and color schemes",
      "Add club branding and sponsor logos",
      "Generate and share your leaderboard",
    ],
    useCase:
      "Perfect for mid-season top performer updates, end-of-season award announcements, celebrating run-scoring champions, wicket-taking leaders, and engaging fans with statistics-driven content.",
  },
  {
    id: "quiz-generator",
    name: "Quiz Generator",
    shortDescription: "Challenge yourself with AI-generated club stats quiz.",
    icon: FaQuestionCircle,
    fullDescription:
      "Create interactive quizzes about your club's statistics, players, and match history. AI-powered quiz generation makes it easy to engage supporters with fun, club-specific trivia challenges.",
    workflow: [
      "Set quiz difficulty level (easy, medium, hard)",
      "Choose number of questions (1-20)",
      "Select top players to include",
      "Pick season or use all-time stats",
      "Generate AI-powered quiz questions",
      "Review and customize questions",
      "Share quiz with your supporters",
    ],
    useCase:
      "Excellent for engaging supporters during off-season, building community interaction, testing fan knowledge, and creating shareable content that encourages engagement on social media.",
  },
  {
    id: "fixtures",
    name: "Fixtures",
    shortDescription: "Select multiple matches and create fixture announcements.",
    icon: FaCalendar,
    fullDescription:
      "Create professional fixture graphics showing upcoming matches, dates, venues, and opponents. Perfect for announcing monthly schedules, key fixtures, or entire season calendars.",
    workflow: [
      "Select multiple upcoming matches",
      "Choose from fixture announcement templates",
      "Customize date formats and display options",
      "Add venue information and opponent details",
      "Adjust styling and layout preferences",
      "Include sponsor branding",
      "Generate and share your fixtures graphic",
    ],
    useCase:
      "Ideal for monthly fixture announcements, highlighting key upcoming matches, creating season calendars, building anticipation for big games, and helping supporters plan their attendance.",
  },
  {
    id: "create-with-ai",
    name: "Create with AI",
    shortDescription: "Generate cricket club posters using AI with natural language prompts.",
    icon: FaRocket,
    fullDescription:
      "Harness the power of AI to create custom cricket club graphics from simple text prompts. Describe what you want, and our AI generates professional designs tailored to your needs.",
    workflow: [
      "Enter a natural language description of your desired graphic",
      "AI generates multiple design options",
      "Select your preferred template design",
      "Customize colors, text, and elements",
      "Fine-tune using the built-in editor",
      "Add sponsor logos and club branding",
      "Generate and share your AI-created graphic",
    ],
    useCase:
      "Perfect for creating unique, one-off graphics for special events, custom announcements, creative promotional content, and any design needs where standard templates don't fit.",
  },
  {
    id: "sponsors",
    name: "Sponsors",
    shortDescription: "Match ball and player sponsors in one place, included in every post.",
    icon: FaHandshake,
    fullDescription:
      "Keep all your sponsor information in one place. Add match ball sponsors and player sponsors once, and Snaptrix automatically includes them in every relevant post—team announcements, match summaries, top performance graphics, and more. No more copying logos or forgetting a sponsor.",
    workflow: [
      "Add match ball sponsors and upload logos in your club settings",
      "Assign player sponsors to individual players on your roster",
      "Link sponsors to specific matches when creating posts",
      "Generate any post—sponsors are automatically included from your one place",
      "Update sponsor details once; changes reflect across all future posts",
    ],
    useCase:
      "Essential for clubs who want to deliver consistent visibility to sponsors, avoid manual logo placement on every graphic, and keep match and player sponsorship management simple and reliable.",
  },
];
